import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import moment, { Moment } from 'moment';
import { PlatformEphemerisService } from '@data/_generated-api/platform-api/services/platform-ephemeris.service';
import {
  EphemerisDailyTable,
  EphemerisMoment,
  EphemerisTable,
  toEphemerisDailyTable,
  toEphemerisMoment,
  toEphemerisTable,
} from './ephemeris.models';

/** Soort fout bij het laden; de component vertaalt ze naar een melding. */
export type EphemerisLoadError = 'rateLimited' | 'invalid' | 'failed';

/** ISO-8601 UTC zoals de API het verwacht (repo-idioom, zie transit-data.service). */
function formatUtcForApi(time: Moment): string {
  return moment.utc(time).format('YYYY-MM-DDTHH:mm:ss[Z]');
}

/**
 * Data-laag van de ephemeris-pagina: haalt een venster op via `GET /api/ephemeris/table`
 * (wissels) of `GET /api/ephemeris/daily` (dagposities) en publiceert de staat als signals.
 *
 * - **Cache per venster en per soort** (zolang de pagina leeft): terug- en vooruitklikken
 *   of wisselen tussen Wissels en Dagposities kost geen nieuwe opvraging. Belangrijk omdat
 *   beide endpoints één strakke rate-limit-bucket delen (10 meteen, daarna 1 per 15 s).
 * - **Bij een fout blijft de vorige data staan** (vooral bij 429): de gebruiker houdt iets
 *   om naar te kijken en ziet de melding erboven.
 * - **Laatste opvraging wint** (over beide soorten heen): een trager antwoord op een ouder
 *   venster overschrijft een nieuwer niet, en zet ook de laad- of foutstatus niet meer.
 *
 * Bewust component-scoped (`providers` van de tabelcomponent): de cache verdwijnt met
 * de pagina.
 */
@Injectable()
export class EphemerisTableDataService {
  private readonly api = inject(PlatformEphemerisService);

  private readonly tableState = signal<EphemerisTable | undefined>(undefined);
  private readonly dailyState = signal<EphemerisDailyTable | undefined>(undefined);
  private readonly loadingState = signal(false);
  private readonly errorState = signal<EphemerisLoadError | undefined>(undefined);
  private readonly errorMessageState = signal<string | undefined>(undefined);

  /** Wissels (lijn-/poortwissels en stations). */
  readonly table = this.tableState.asReadonly();
  /** Dagposities (00:00 UT, de Maan ook 12:00 UT). */
  readonly daily = this.dailyState.asReadonly();
  /** Een tabel of een ingevoegd moment wordt geladen. */
  readonly loading = computed(() => this.loadingState() || this.pendingMoments() > 0);
  readonly error = this.errorState.asReadonly();
  /** Foutmelding van de backend bij `invalid` (400). */
  readonly errorMessage = this.errorMessageState.asReadonly();

  /** Posities op de door de gebruiker ingevoegde momenten, per ISO-UTC-sleutel. */
  private readonly momentsState = signal<ReadonlyMap<string, EphemerisMoment>>(new Map());
  readonly moments = this.momentsState.asReadonly();
  private readonly momentRequests = new Set<string>();
  /** Lopende momentopvragingen (los van de tabelstatus, die de laatste opvraging volgt). */
  private readonly pendingMoments = signal(0);

  private readonly tableCache = new Map<string, EphemerisTable>();
  private readonly dailyCache = new Map<string, EphemerisDailyTable>();
  private requestSequence = 0;

  load(fromUtc: Moment, toUtc: Moment): Promise<void> {
    return this.fetch(fromUtc, toUtc, '', this.tableCache, this.tableState, params =>
      this.api.getEphemerisTable(params), toEphemerisTable);
  }

  /**
   * Dagposities om 00:00 en 12:00 in `timeZone` (IANA, bv. `Europe/Brussels` of `UTC`):
   * de backend rekent op die lokale momenten, niet enkel de weergave verschuift.
   */
  loadDaily(fromUtc: Moment, toUtc: Moment, timeZone: string): Promise<void> {
    return this.fetch(fromUtc, toUtc, timeZone, this.dailyCache, this.dailyState, params =>
      this.api.getEphemerisDaily({ ...params, timeZone }), toEphemerisDailyTable);
  }

  /**
   * Posities op één ingevoegd moment (`GET /api/ephemeris/moment`), één keer per moment.
   * Fouten gaan via dezelfde foutstatus als de tabellen (bv. 429); een mislukt moment mag
   * later opnieuw geprobeerd worden.
   */
  async loadMoment(atUtc: Moment): Promise<void> {
    const key = formatUtcForApi(atUtc);
    if (this.momentsState().has(key) || this.momentRequests.has(key)) return;
    this.momentRequests.add(key);
    this.pendingMoments.update(count => count + 1);
    try {
      const result = toEphemerisMoment(await firstValueFrom(this.api.getEphemerisMoment({ atUtc: key })));
      this.momentsState.update(current => new Map(current).set(key, result));
    } catch (error) {
      this.setError(error);
    } finally {
      this.momentRequests.delete(key);
      this.pendingMoments.update(count => count - 1);
    }
  }

  private async fetch<TDto, TModel>(
    fromUtc: Moment,
    toUtc: Moment,
    variant: string,
    cache: Map<string, TModel>,
    state: { set(value: TModel): void },
    request: (params: { fromUtc: string; toUtc: string }) => Observable<TDto>,
    map: (dto: TDto) => TModel,
  ): Promise<void> {
    const params = { fromUtc: formatUtcForApi(fromUtc), toUtc: formatUtcForApi(toUtc) };
    const cacheKey = `${params.fromUtc}|${params.toUtc}|${variant}`;
    const sequence = ++this.requestSequence;

    const cached = cache.get(cacheKey);
    if (cached) {
      state.set(cached);
      this.errorState.set(undefined);
      this.loadingState.set(false);
      return;
    }

    this.loadingState.set(true);
    try {
      const model = map(await firstValueFrom(request(params)));
      cache.set(cacheKey, model);
      if (sequence !== this.requestSequence) return;
      state.set(model);
      this.errorState.set(undefined);
      this.errorMessageState.set(undefined);
    } catch (error) {
      if (sequence !== this.requestSequence) return;
      this.setError(error);
    } finally {
      if (sequence === this.requestSequence) {
        this.loadingState.set(false);
      }
    }
  }

  private setError(error: unknown): void {
    const httpError = error instanceof HttpErrorResponse ? error : undefined;
    if (httpError?.status === 429) {
      this.errorState.set('rateLimited');
      this.errorMessageState.set(undefined);
      return;
    }
    if (httpError?.status === 400) {
      const body = httpError.error as { error?: unknown } | null | undefined;
      this.errorState.set('invalid');
      this.errorMessageState.set(typeof body?.error === 'string' ? body.error : undefined);
      return;
    }
    this.errorState.set('failed');
    this.errorMessageState.set(undefined);
  }
}
